import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { DatePicker, InputNumber, Select } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { colours } from '@qflow/theme'

import { AddButton } from 'portal/components/RecordsModules/common'
import { getBrowserShortDateFormat } from 'portal/lib/functions'

export const AddNewMilestone = ({
  isImperial,
  weightUnit,
  volumeUnit,
  onAddMilestone
}) => {
  const [productGroup, setProductGroup] = useState<string | null>(null)
  const [date, setDate] = useState<Dayjs | null>(null)
  const [weightValue, setWeightValue] = useState<number | null>(null)
  const [volumeValue, setVolumeValue] = useState<number | null>(null)

  const allProductGroups = useSelector((state: any) => state.productGroups)

  // Only dates after today can be selected
  const disabledDate = (current: Dayjs) => !current.isAfter(dayjs(), 'day')

  const availableOptions: { value: string; label: string }[] = useMemo(
    () =>
      Array.isArray(allProductGroups.data)
        ? allProductGroups.data.map(pg => ({
            label: pg.name,
            value: pg.productGroupId ? pg.productGroupId : '',
            key: pg.productGroupId ? pg.productGroupId : ''
          }))
        : [],
    [allProductGroups]
  )

  const handleAddMilestone = () => {
    if (!productGroup || !date || (!weightValue && !volumeValue)) {
      return
    }

    onAddMilestone({
      milestoneId: uuidv4(),
      productGroup,
      milestoneDate: date.format('YYYY-MM-DD'),
      weight: weightValue,
      weightUnit: isImperial ? 'Pound' : 'Tonne',
      volume: volumeValue,
      volumeUnit: isImperial ? 'CubicYard' : 'CubicMeter'
    })

    // Reset the form after adding the milestone
    setProductGroup(null)
    setDate(null)
    setWeightValue(null)
    setVolumeValue(null)
  }

  return (
    <MilestoneContainer>
      <div>
        <MilestoneContainerText>Product Group</MilestoneContainerText>
        <StyledSelect
          id={'productGroupSelect'}
          value={productGroup}
          allowClear
          showSearch
          placeholder="Select product group"
          options={availableOptions}
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={(value: any) => setProductGroup(value)}
        />
      </div>
      <div>
        <MilestoneContainerText>Date</MilestoneContainerText>
        <DatePicker
          id="milestoneDate"
          style={{ width: '100%' }}
          format={getBrowserShortDateFormat()}
          value={date}
          onChange={value => setDate(value)}
          disabledDate={disabledDate}
          placeholder="Select date"
        />
      </div>
      <WeightVolumeContainer>
        <div>
          <MilestoneContainerText>
            Minimum Weight ({weightUnit})
          </MilestoneContainerText>
          <InputNumber
            style={{ width: '100%' }}
            value={weightValue}
            onChange={value => setWeightValue(value)}
            placeholder="Enter weight"
            min={0}
            disabled={volumeValue ? true : false}
          />
        </div>
        <div>
          <MilestoneContainerText>
            Minimum Volume ({volumeUnit})
          </MilestoneContainerText>
          <InputNumber
            style={{ width: '100%' }}
            value={volumeValue}
            onChange={value => setVolumeValue(value)}
            placeholder="Enter volume"
            min={0}
            disabled={weightValue ? true : false}
          />
        </div>
      </WeightVolumeContainer>
      <ButtonContainer>
        <AddButton
          disabled={!productGroup || !date || (!weightValue && !volumeValue)}
          onClick={handleAddMilestone}
        >
          Add Milestone
        </AddButton>
      </ButtonContainer>
    </MilestoneContainer>
  )
}

const MilestoneContainer = styled.div`
  background-color: ${colours.WHITE};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid ${colours.CLOUD_GREY};
  padding: 0.75rem;
  margin-top: 0.25rem;
  width: 442px;
`
const MilestoneContainerText = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
`
const StyledSelect = styled(Select)`
  width: 100%;
`
const WeightVolumeContainer = styled.div`
  display: flex;
  gap: 1rem;

  > div {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`
const ButtonContainer = styled.div`
  display: flex;
  gap: 0.625rem;
  justify-content: flex-end;
`
